/**
 * Inline Formatting value objects — see GLOSSARY.md → Formatting Mark,
 * Editing-Hint Highlight, Inline Formatting (F-049 / UAT-21).
 *
 * Pure, zero-infra (Constitution §III). Offsets are 0-based Unicode CODE
 * POINTS into a Content Item body, half-open `[offset, offset + length)`.
 * These value objects carry NO body text — offsets only (§IX, not @sensitive).
 */

/**
 * Bold / Italic / Underline — the user-editable, exported emphasis set.
 * Strikethrough is deliberately excluded (FR-011); Highlight is NOT here (it is
 * the read-only, never-exported {@link EditingHintHighlight}).
 */
export const FORMATTING_MARK_KINDS = ['bold', 'italic', 'underline'] as const;
export type FormattingMarkKind = (typeof FORMATTING_MARK_KINDS)[number];

export const isFormattingMarkKind = (raw: unknown): raw is FormattingMarkKind =>
  typeof raw === 'string' && (FORMATTING_MARK_KINDS as readonly string[]).includes(raw);

/** A half-open code-point span `[offset, offset + length)` over a body. */
export type Span = {
  readonly offset: number;
  readonly length: number;
};

/** A user-editable, exported inline emphasis (B/I/U) over a span of a body. */
export type FormattingMark = Span & {
  readonly kind: FormattingMarkKind;
};

/**
 * A template-derived, read-only, never-exported editing-hint highlight. The
 * optional `colour` is captured source metadata only (not the rendering carrier
 * — the in-app aid is a single colour-independent "needs-review" treatment).
 */
export type EditingHintHighlight = Span & {
  readonly colour?: string;
};

/**
 * F-114 (#114): where a placeholder's value comes from — see GLOSSARY.md →
 * Placeholder Source-Type. Mirrors the Smithy `PlaceholderSourceType`, extending
 * the render taxonomy (identity|terminology|choice) with author free-text and the
 * unmapped fallback. Exactly one per {@link PlaceholderSpan} (SC-003).
 */
export const PLACEHOLDER_SOURCE_TYPES = [
  'identity',
  'terminology',
  'choice',
  'free_text',
  'unknown',
] as const;
export type PlaceholderSourceType = (typeof PLACEHOLDER_SOURCE_TYPES)[number];

export const isPlaceholderSourceType = (raw: unknown): raw is PlaceholderSourceType =>
  typeof raw === 'string' && (PLACEHOLDER_SOURCE_TYPES as readonly string[]).includes(raw);

/**
 * F-114 (#114): one placeholder occurrence over a General Instructions body — a
 * {@link Span} plus the catalog identity and the denormalised display name +
 * source type, so the renderer needs no catalog at runtime (FR-016/FR-017). The
 * stored body stays raw (tokens intact); values are injected only at render (see
 * `resolvePlaceholders`). Template-derived, version-pinned, never exported.
 */
export type PlaceholderSpan = Span & {
  readonly canonicalId: string;
  readonly displayName: string; // @sensitive — carries trial terminology (§IX)
  readonly sourceType: PlaceholderSourceType;
};

/**
 * F-114 (#114): the per-trial resolved value map supplied to the GI display at
 * render time — `canonicalId → resolved value`. A `canonicalId` absent from the
 * map (or the whole map absent) ⇒ the placeholder renders its display name, never
 * a raw `{…}` token (FR-018). Supplying a different map re-renders the same stored
 * prose with new values (FR-019).
 */
export type PlaceholderValues = ReadonlyMap<string, string>;
